import {IdynamicFormItem} from "@ispm/dynamic-form/interfaces/IdynamicFormItem";
import {REPLACE_ACTION_TYPES} from "@ispm-client/modules/aggregator/services/consts";
import {filter, get, has, isArray, isEmpty, set} from "lodash";
import {IdataBinding, IreplaceAction} from "@ispm-client/modules/aggregator/interfaces";
import {ITEMS_TYPES} from "@ispm/dynamic-form/constants/ItemsTypes";
import {IoptionElemet} from "@ispm/shared/interfaces/IoptionElemet";

const REPLACE_ACTION = 'replaceAction';

export class ReplaceActionService {
  public static replace(dataBindings: Array<IdataBinding>, formData: any, formItem: IdynamicFormItem) {
    if (!!dataBindings && !!formData) {
      const element = dataBindings.find(element => element.identifier === formItem.name);
      if (!!element && has(element, REPLACE_ACTION)) {
        const replaceAction: IreplaceAction = get(element, REPLACE_ACTION);
        switch (replaceAction.actionType) {
          case REPLACE_ACTION_TYPES.SET_TO_PROPERTY:
            this.setProperty(replaceAction, formData, formItem);
            break;
          case REPLACE_ACTION_TYPES.SET_TO_OPTIONS:
            this.setData(replaceAction, formData, formItem);
            break;
          case REPLACE_ACTION_TYPES.SET_TO_OPTIONS_RESOURCE:
            const valuesFromFormData = get(formData, replaceAction.dataFromPath);
            set(formItem, 'options', {resource: valuesFromFormData, values: null});
            this.replaceDefault(replaceAction, valuesFromFormData, formItem);
            break;
          default:
            break;
        }
      }
    }
  }

  private static setProperty(actionDescr: IreplaceAction, formData: any, formItem: IdynamicFormItem) {
    const valuesFromFormData = get(formData, actionDescr.dataFromPath) || actionDescr.defaultValue || undefined;
    let propertyValue = valuesFromFormData;

    if (valuesFromFormData instanceof Map) {
      //TODO: Only one nesting level is available for the SET_TO_PROPERTY action,
      // so if transform is "Test.offPathReasonDetailsRequired" - Test is the key in the Map
      // and offPathReasonDetailsRequired is the property in the Map object
      const trans = actionDescr.transform.split('.');
      if (trans.length === 2) {
        propertyValue = valuesFromFormData.get(trans[0])[trans[1]];
      } else {
        propertyValue = undefined;
      }
    }

    if (propertyValue) {
      set(formItem, actionDescr.targetField, propertyValue);
    }
  }

  private static setData(actionDescr: IreplaceAction, formData: any, formItem: IdynamicFormItem, attributeToAdd: string = null) {
    if (!isEmpty(formData) && (!formItem.defaultValue || !formItem.options.values)) {
      let valuesFromFormData = get(formData, actionDescr.dataFromPath);
      if (!valuesFromFormData && has(actionDescr, 'fallbackPath')) {
        valuesFromFormData = get(formData, actionDescr.fallbackPath);
      }
      if (!!valuesFromFormData) {
        const transformDescr = get(actionDescr, 'transform');
        switch (formItem.type.toString().toUpperCase()) {
          case ITEMS_TYPES.MULTISELECT:
            const values = [];
            const transformData = !!transformDescr ? get(formData, actionDescr.transform) : null;
            valuesFromFormData.split(',').forEach((substr: string) => {
              const value = {code: substr, display: !!transformData ? get(transformData, substr, substr) : substr};
              return values.push(value);
            });
            set(formItem, 'options', {resource: null, values: values});
            if (!formItem.defaultValue)
              this.setDefaultValue(formData, formItem, actionDescr, values);
            break;
          case ITEMS_TYPES.WIDGET:
            const dataTransformMapToOptions = get(actionDescr, 'dataTransformMapToOptions');
            if (dataTransformMapToOptions) {
              const values: any[] = this.transformDataToOptions(actionDescr, valuesFromFormData);
              formItem.options.values = values as Array<IoptionElemet | string>;
              if (!formItem.defaultValue)
                this.setDefaultValue(formData, formItem, actionDescr, values);
            }
            break;
          default:
            set(formItem, 'options', {resource: null, values: valuesFromFormData});
            this.replaceDefault(actionDescr, valuesFromFormData, formItem);
            break;
        }
      }
    }
  }

  private static transformDataToOptions(actionDescr: IreplaceAction, data: any) {
    let values = [];
    const dataTransformMapToOptions = get(actionDescr, 'dataTransformMapToOptions');
    const dataFilter = get(actionDescr, 'dataFilter');
    if (dataFilter) {
      data = filter(data, (item) => {
        for (let key in dataFilter) {
          if (item && item[key] && item[key] === dataFilter[key]) {
            return item;
          }
        }
      });
    }

    if (isArray(data)) {
      values = data.map((obj) => {
        if (obj[dataTransformMapToOptions.code] && obj[dataTransformMapToOptions.display]) {
          return {
            "code": obj[dataTransformMapToOptions.code],
            "display": obj[dataTransformMapToOptions.display]
          }
        }
      });
    } else {
      Object.keys(data).forEach(key => {
        if (data[key][dataTransformMapToOptions.code] && data[key][dataTransformMapToOptions.display])
          values.push({
            "code": data[key][dataTransformMapToOptions.code],
            "display": data[key][dataTransformMapToOptions.display]
          })
      });
    }

    return values;
  }

  private static setDefaultValue(formData: any, formItem: IdynamicFormItem, actionDescr: IreplaceAction, values: any[]) {
    if (has(actionDescr, 'defaultValuePath')) {
      const defaultValue: any = get(formData, actionDescr.defaultValuePath);
      set(formItem, 'defaultValuePath', [values.find(item => item.code === defaultValue)]);
    } else if (has(actionDescr, 'defaultIndex')) {
      const defaultIndex = actionDescr.defaultIndex;
      if (defaultIndex < 0 || defaultIndex > values.length)
        set(formItem, 'defaultValue', values);
      else {
        set(formItem, 'defaultValue', [values[defaultIndex < values.length && defaultIndex >= 0 ? defaultIndex : 0]]);
      }
    }
  }

  private static replaceDefault(actionDescr: IreplaceAction, valuesFromFormData: any, formItem: IdynamicFormItem) {
    if (has(actionDescr, 'defaultIndex') && actionDescr.defaultIndex >= 0) {
      const index = get(actionDescr, 'defaultIndex', 0);
      if (valuesFromFormData instanceof Array && valuesFromFormData.length > index) {
        set(formItem, 'defaultValue', valuesFromFormData[index]);
      }
    }
  }
}
